import React from 'react';
import { motion } from 'framer-motion';
import { CoopQuizSession } from '../hooks/useCoopQuiz';

interface CoopQuizLobbyProps {
  session: CoopQuizSession;
  myPlayerNumber: 1 | 2 | null;
  onJoinAsPlayer: (playerNum: 1 | 2) => void;
  onSetReady: () => void;
  onClose: () => void;
  getPlayer1DisplayData: () => { displayName: string; profileImage: string };
  getPlayer2DisplayData: () => { displayName: string; profileImage: string };
}

export const CoopQuizLobby: React.FC<CoopQuizLobbyProps> = ({
  session,
  myPlayerNumber,
  onJoinAsPlayer,
  onSetReady,
  onClose,
  getPlayer1DisplayData,
  getPlayer2DisplayData
}) => {
  const player1 = session.players.player1;
  const player2 = session.players.player2;

  // Get display data separately from session data
  const player1Display = getPlayer1DisplayData();
  const player2Display = getPlayer2DisplayData();

  const canStartGame = player1.joined && player1.ready && player2.joined && player2.ready;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-purple-400 mb-2">
          Lobby Co-op
        </h2>
        <p className="text-gray-400 text-sm">
          Selecciona tu usuario para empezar
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Session ID: {session.sessionId}
        </p>
      </div>

      {/* Player Slots */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Player 1 Slot */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`p-6 rounded-lg border-2 transition-all ${
            player1.joined
              ? 'bg-green-500/20 border-green-500'
              : 'bg-white/5 border-white/20 hover:border-green-500/50'
          }`}
        >
          <div className="text-center space-y-4">
            <div className="relative inline-block">
              <img
                src={player1Display.profileImage}
                alt={player1Display.displayName}
                className="w-24 h-24 rounded-full border-4 border-green-400 mx-auto"
              />
              {player1.joined && (
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  {player1.ready ? '✅' : '⏳'}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-xl font-bold text-green-400">
                {player1Display.displayName}
              </h3>
              <p className="text-sm text-gray-400">
                {player1.joined ? (player1.ready ? 'Listo!' : 'Esperando...') : 'Disponible'}
              </p>
            </div>

            {!player1.joined && !myPlayerNumber && (
              <button
                onClick={() => onJoinAsPlayer(1)}
                className="px-6 py-3 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition-all hover:scale-105"
              >
                Seleccionar Usuario 1
              </button>
            )}

            {player1.joined && myPlayerNumber === 1 && !player1.ready && (
              <button
                onClick={onSetReady}
                className="px-6 py-3 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition-all hover:scale-105"
              >
                Listo
              </button>
            )}
          </div>
        </motion.div>

        {/* Player 2 Slot */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`p-6 rounded-lg border-2 transition-all ${
            player2.joined
              ? 'bg-pink-500/20 border-pink-500'
              : 'bg-white/5 border-white/20 hover:border-pink-500/50'
          }`}
        >
          <div className="text-center space-y-4">
            <div className="relative inline-block">
              <img
                src={player2Display.profileImage}
                alt={player2Display.displayName}
                className="w-24 h-24 rounded-full border-4 border-pink-400 mx-auto"
              />
              {player2.joined && (
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center">
                  {player2.ready ? '✅' : '⏳'}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-xl font-bold text-pink-400">
                {player2Display.displayName}
              </h3>
              <p className="text-sm text-gray-400">
                {player2.joined ? (player2.ready ? 'Listo!' : 'Esperando...') : 'Disponible'}
              </p>
            </div>

            {!player2.joined && !myPlayerNumber && (
              <button
                onClick={() => onJoinAsPlayer(2)}
                className="px-6 py-3 bg-pink-500 text-white rounded-full font-bold hover:bg-pink-600 transition-all hover:scale-105"
              >
                Seleccionar Usuario 2
              </button>
            )}

            {player2.joined && myPlayerNumber === 2 && !player2.ready && (
              <button
                onClick={onSetReady}
                className="px-6 py-3 bg-pink-500 text-white rounded-full font-bold hover:bg-pink-600 transition-all hover:scale-105"
              >
                Listo
              </button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Status Message */}
      {canStartGame && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-4 bg-purple-500/20 rounded-lg border border-purple-500/50"
        >
          <p className="text-lg font-bold text-purple-400">
            ¡Ambos jugadores están listos! El juego comenzará pronto...
          </p>
        </motion.div>
      )}

      {myPlayerNumber && !canStartGame && (
        <div className="text-center text-gray-400 text-sm">
          {!player1.joined || !player2.joined
            ? 'Esperando a que ambos jugadores se unan...'
            : !player1.ready || !player2.ready
            ? 'Esperando a que ambos jugadores estén listos...'
            : null}
        </div>
      )}

      {/* Close Buttons */}
      <div className="flex justify-center gap-4">
        <button
          onClick={onClose}
          className="px-6 py-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-all text-sm"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};
